// ── Deployment target ───────────────────────────────────────────────────
// ONE switch decides which backend the whole app talks to. Existing builds may
// set it at build time. Release builds load it from /runtime-config.js so TEST
// and production can run byte-identical application bundles:
//
//   npm run build:prod           → live site (prod gateway + `types`)
//   npm run build:proposal-test  → dedicated TEST; explicit TEST API required
//   npm run build:test           → legacy QA (shared dev gateway + `devtypes`)
//
// Plain `npm run build` and local `npm start` leave the var unset and fall
// back to the legacy test/dev backend. Dedicated TEST uses the `proposal-test`
// mode plus REACT_APP_API_GATEWAY_URL and an isolated forms bucket; it never
// falls back silently to the legacy API.
const runtimeConfig =
  typeof window !== "undefined" && window.__VICUS_RUNTIME_CONFIG__
    ? window.__VICUS_RUNTIME_CONFIG__
    : {};
const deployEnv =
  runtimeConfig.deployEnv || process.env.REACT_APP_DEPLOY_ENV || "test";
const isProd = deployEnv === "prod";
const isProposalTest = deployEnv === "proposal-test";
const explicitApiGatewayUrl =
  runtimeConfig.apiGatewayUrl || process.env.REACT_APP_API_GATEWAY_URL;
const supportApiGatewayUrl =
  runtimeConfig.supportApiGatewayUrl ||
  process.env.REACT_APP_SUPPORT_API_GATEWAY_URL ||
  "https://mzeotwuux2.execute-api.us-east-2.amazonaws.com/shared";
const proposalApiVersion =
  runtimeConfig.proposalApiVersion ||
  process.env.REACT_APP_PROPOSAL_API_VERSION ||
  (isProposalTest ? "m06niq" : isProd ? "vz7qvw" : "legacy-dev");
const proposalTestApiPlaceholder =
  "https://REPLACE_WITH_PROPOSAL_GENERATOR_TEST_API.execute-api.us-east-2.amazonaws.com/test";
const formsBucket = isProposalTest
  ? runtimeConfig.formsBucket ||
    process.env.REACT_APP_FORMS_BUCKET ||
    "vicus-proposal-forms-test"
  : runtimeConfig.formsBucket || "vicus-proposal-forms";
const defaultLoginOrigin = isProposalTest
  ? "https://login-test.vicuscapital.com"
  : "https://login.vicuscapital.com";
const loginOrigin = runtimeConfig.loginPortalOrigin || defaultLoginOrigin;
const loginPortal = {
  origin: loginOrigin,
  host: new URL(loginOrigin).host,
};
const defaultCognito = isProposalTest
  ? {
      REGION: "us-east-2",
      USER_POOL_ID: "us-east-2_LOYzLy1zt",
      APP_CLIENT_ID: "36mkf5fmckfm451v9jq14qlg4",
      IDENTITY_POOL_ID: "us-east-2:5b011f8b-9c8b-4abe-81c3-7b01bf8e507d",
    }
  : {
      REGION: "us-east-2",
      USER_POOL_ID: "us-east-2_LOYzLy1zt",
      APP_CLIENT_ID: "1j4fbvhn7gqhsutobj5peqr8se",
      IDENTITY_POOL_ID: "us-east-2:6c088227-436a-4e08-a100-392e33b1b164",
    };
const cognito = { ...defaultCognito, ...(runtimeConfig.cognito || {}) };

if (process.env.REACT_APP_DEPLOY_ENV === "runtime") {
  const requiredRuntimeValues = {
    deployEnv: runtimeConfig.deployEnv,
    apiGatewayUrl: runtimeConfig.apiGatewayUrl,
    supportApiGatewayUrl: runtimeConfig.supportApiGatewayUrl,
    proposalApiVersion: runtimeConfig.proposalApiVersion,
    formsBucket: runtimeConfig.formsBucket,
    loginPortalOrigin: runtimeConfig.loginPortalOrigin,
    cognitoRegion: runtimeConfig.cognito && runtimeConfig.cognito.REGION,
    cognitoUserPool: runtimeConfig.cognito && runtimeConfig.cognito.USER_POOL_ID,
    cognitoClient: runtimeConfig.cognito && runtimeConfig.cognito.APP_CLIENT_ID,
    cognitoIdentityPool:
      runtimeConfig.cognito && runtimeConfig.cognito.IDENTITY_POOL_ID,
  };
  const missing = Object.entries(requiredRuntimeValues)
    .filter(([, value]) => !value)
    .map(([name]) => name);
  if (runtimeConfig.schemaVersion !== 1 || missing.length) {
    throw new Error(
      `Invalid Proposal Generator runtime configuration: ${missing.join(", ")}`
    );
  }
}

const config = {
  MAX_ATTACHMENT_SIZE: 60 * 1000000, // number * 1MB
  FORMS_BUCKET: formsBucket,
  deployEnv,
  isProd,
  isProposalTest,
  // proposal-types DynamoDB table: live `types` in prod, `devtypes` elsewhere.
  proposalTypesType:
    runtimeConfig.proposalTypesType || (isProd ? "types" : "devtypes"),
  s3: {
    REGION: "us-east-2",
    BUCKET: formsBucket,
  },
  apiGateway: {
    REGION: "us-east-2",
    URL:
      explicitApiGatewayUrl ||
      (isProd
        ? "https://8lvg9c2kw8.execute-api.us-east-2.amazonaws.com/prod"
        : isProposalTest
        ? proposalTestApiPlaceholder
        : "https://8lvg9c2kw8.execute-api.us-east-2.amazonaws.com/dev"),
    FILELIMIT: 4194304,
    version: proposalApiVersion,
  },
  supportApi: {
    URL: supportApiGatewayUrl,
    reportedEnvironment: isProposalTest ? "test" : "production",
  },
  cognito,
  loginPortal: {
    ...loginPortal,
    logoutUrl: `${loginPortal.origin}/logout.html`,
  },
  sessionLock: {
    enabled:
      runtimeConfig.sessionLockEnabled !== undefined
        ? runtimeConfig.sessionLockEnabled
        : process.env.REACT_APP_SESSION_LOCK_ENABLED !== "false",
    group:
      runtimeConfig.sessionLockGroup ||
      process.env.REACT_APP_SESSION_LOCK_GROUP ||
      "proposal-generator",
    cookieDomain:
      runtimeConfig.sessionCookieDomain ||
      process.env.REACT_APP_SESSION_LOCK_COOKIE_DOMAIN ||
      (process.env.NODE_ENV === "production" ? ".vicuscapital.com" : ""),
    isLoginPortal: process.env.REACT_APP_IS_LOGIN_PORTAL === "true",
    loginPortalHost: loginPortal.host,
    warningMs: 13 * 60 * 1000,
    timeoutMs: 15 * 60 * 1000,
    presenceStaleMs: 5 * 60 * 1000,
    activityThrottleMs: 1000,
  },
  sessionEdge: {
    enabled:
      (runtimeConfig.sessionEdgeEnabled !== undefined
        ? runtimeConfig.sessionEdgeEnabled
        : process.env.REACT_APP_VC_SESSION_EDGE_ENABLED === "true") &&
      (runtimeConfig.sessionLockEnabled !== undefined
        ? runtimeConfig.sessionLockEnabled
        : process.env.REACT_APP_SESSION_LOCK_ENABLED !== "false"),
    cookieName: runtimeConfig.sessionEdgeCookieName || "vc_last_activity",
    cookieDomain:
      runtimeConfig.sessionCookieDomain ||
      process.env.REACT_APP_SESSION_LOCK_COOKIE_DOMAIN ||
      (process.env.NODE_ENV === "production" ? ".vicuscapital.com" : ""),
    heartbeatPath: "/_/heartbeat",
    heartbeatThrottleMs: 60 * 1000,
  },
};
export default config;
